/* ──────────────────────────────────────────────────────────────
   pages/admin/manager/documentos/EnviarDocumentoPage.tsx
   Pantalla “Enviar documento”  +  comentarios + sincronización Drive
   ────────────────────────────────────────────────────────────── */

import { useState, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Stack,
  Grid,
  Paper,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  PictureAsPdf,
  Delete,
  GroupAdd,
  UploadFile,
  Sync, // ⬅️ nuevo
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "../../../../context/AuthContext";
import { useDocumentTypes } from "../../../../hooks/documentos/useDocumentTypes";
import { useUsersSearch } from "../../../../hooks/documentos/useUsersSearch"; // ← sin cambios
import { useUploadPdf } from "../../../../hooks/documentos/useUploadPdf";
import { useSendDocument } from "../../../../hooks/documentos/useSendDocument";
import RecipientsAutocomplete from "../../../../hooks/documentos/RecipientsAutocomplete";
import api from "../../../../api/axios";

/* --------------------------------------------------
   Tipos mínimos
-------------------------------------------------- */
interface UserLite {
  id: number;
  email: string;
  avatarUrl: string | null;
}

/* --------------------------------------------------
   Grupos de destinatarios
-------------------------------------------------- */
interface RoleGroup {
  key: string;
  label: string;
  roles: string[];
}

const ALL_ROLES = [
  "admin",
  "facturacion",
  "att_familias",
  "att_alumnado",
  "att_profesorado",
  "admisiones",
  "hr",
  "profesor",
  "consultor",
  "cliente",
];

const ROLE_GROUPS: RoleGroup[] = [
  {
    key: "empleados",
    label: "Empleados",
    roles: ALL_ROLES.filter((r) => r !== "cliente"),
  },
  {
    key: "staff",
    label: "Staff",
    roles: ALL_ROLES.filter((r) => !["cliente", "profesor"].includes(r)),
  },
  { key: "profesor", label: "Profesor", roles: ["profesor"] },
];

/* --------------------------------------------------
   Diálogo “Añadir por rol / grupo”
-------------------------------------------------- */
function AddByRoleDialog({
  open,
  onClose,
  onAdded,
}: {
  open: boolean;
  onClose: () => void;
  onAdded: (u: UserLite[]) => void;
}) {
  const [checked, setChecked] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const toggle = (g: string) =>
    setChecked((prev) => (prev.includes(g) ? prev.filter((k) => k !== g) : [...prev, g]));

  const handleAdd = async () => {
    setLoading(true);
    try {
      const wantedRoles = ROLE_GROUPS.filter((g) => checked.includes(g.key)).flatMap(
        (g) => g.roles
      );

      /* ejemplo simple → consulta /usuarios?rol=... (1 request por rol) */
      const lists = await Promise.all(
        wantedRoles.map((r) =>
          api.get<UserLite[]>("/usuarios", { params: { rol: r } }).then((res) => res.data)
        )
      );
      onAdded(lists.flat());
      onClose();
    } finally {
      setLoading(false);
      setChecked([]);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Seleccionar grupos de destinatarios</DialogTitle>
      <DialogContent dividers>
        {ROLE_GROUPS.map((g) => (
          <FormControlLabel
            key={g.key}
            control={<Checkbox checked={checked.includes(g.key)} onChange={() => toggle(g.key)} />}
            label={g.label}
          />
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleAdd} disabled={!checked.length || loading}>
          {loading && <CircularProgress size={16} sx={{ mr: 1 }} />}
          Añadir
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* --------------------------------------------------
   Componente principal
-------------------------------------------------- */
export default function EnviarDocumentoPage() {
  const { user } = useAuth();
  const roles = user?.roles ?? [];
  const theme = useTheme();

  /* rol del usuario para mostrar campo interno (debe estar antes de usarlo) */
  const CAN_INTERNAL = roles.some((r) => ["admin", "hr", "facturacion"].includes(r as string));

  /* ---------- 1. Estado local ---------- */
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileErr] = useState<string | null>(null);
  const [docTypeId, setDocType] = useState<number | "">("");
  const [docTypeError, setDocTypeErr] = useState<string | null>(null);
  const [recipients, setRecipients] = useState<UserLite[]>([]);
  const [recipError, setRecipErr] = useState<string | null>(null);

  /* NUEVO → comentarios */
  const [comentario, setComentario] = useState("");
  const [comentarioInt, setComentarioInt] = useState("");

  const [roleDlg, setRoleDlg] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    sev: "success" as "success" | "error",
  });

  // ⬇️ NUEVO — diálogo de sincronización
  const [syncDlg, setSyncDlg] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncEmail, setSyncEmail] = useState("");
  const [syncCreateTypes, setSyncCreateTypes] = useState(true);

  const fileRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  /* ---------- 2. Queries ---------- */
  const { data: docTypes = [], isLoading: typesLoading } = useDocumentTypes();
  const selectedType = docTypes.find((t) => t.docTypeId === docTypeId);
  const singleRecipient = !!selectedType?.needsSignature;

  /* ---------- 3. Mutations ---------- */
  const uploadMut = useUploadPdf();
  const sendMut = useSendDocument();

  /* ---------- 4. Handlers ---------- */
  const showSnack = (m: string, s: "success" | "error") => setSnack({ open: true, msg: m, sev: s });

  const pickFile = (f: File | null) => {
    if (!f) return;
    if (f.type !== "application/pdf") {
      setFileErr("Sólo se permiten PDF");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setFileErr("Máx 10 MB");
      return;
    }
    setFile(f);
    setFileErr(null);
  };

  const dragOver = useCallback((e: DragEvent) => e.preventDefault(), []);
  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    pickFile(e.dataTransfer?.files?.[0] ?? null);
  };

  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    el.addEventListener("dragover", dragOver);
    el.addEventListener("drop", handleDrop);
    return () => {
      el.removeEventListener("dragover", dragOver);
      el.removeEventListener("drop", handleDrop);
    };
  }, [dragOver]);

  /* Limitar a 1 destinatario si el tipo requiere firma */
  useEffect(() => {
    if (singleRecipient && recipients.length > 1) {
      setRecipients([recipients[0]]);
    }
  }, [singleRecipient]); // eslint-disable-line react-hooks/exhaustive-deps

  /* util para añadir muchos recipientes sin duplicar */
  const addManyRecipients = (us: UserLite[]) => {
    setRecipients((prev) => {
      const ids = new Set(prev.map((p) => p.id));
      return [...prev, ...us.filter((u) => !ids.has(u.id))];
    });
  };

  /* Añadir por grupo respetando singleRecipient */
  const handleAddByGroup = (us: UserLite[]) => {
    if (singleRecipient) {
      setRecipients((prev) => (prev.length ? prev : us.slice(0, 1)));
    } else {
      addManyRecipients(us);
    }
  };

  // ⬇️ NUEVO — abrir diálogo de sync, pre-rellenando email
  const openSyncDlg = () => {
    setSyncEmail(recipients[0]?.email || user?.email || "");
    setSyncDlg(true);
  };

  // ⬇️ NUEVO — ejecutar sincronización
  const handleSync = async () => {
    if (!syncEmail.trim()) {
      showSnack("Introduce un email válido", "error");
      return;
    }
    setSyncLoading(true);
    try {
      const { data } = await api.post("/documentos/sync/user", {
        email: syncEmail.trim(),
        createMissingTypes: syncCreateTypes,
      });
      showSnack(
        `Sincronizado: +${data.created} · ya indexados: ${data.skipped} · errores: ${data.errors}`,
        "success"
      );
      setSyncDlg(false);
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || "Error al sincronizar";
      showSnack(msg, "error");
    } finally {
      setSyncLoading(false);
    }
  };

  /* ---------- enviar ---------- */
  const handleSend = async () => {
    let ok = true;
    if (!file) {
      setFileErr("Selecciona un PDF");
      ok = false;
    }
    if (!docTypeId) {
      setDocTypeErr("Selecciona tipo");
      ok = false;
    }
    if (!recipients.length) {
      setRecipErr("Añade destinatario");
      ok = false;
    }
    if (!ok) return;

    try {
      /* 1. sube el PDF */
      const uploaderId = user?.id ?? Number(localStorage.getItem("uid"));
      const { docId } = await uploadMut.mutateAsync({
        file: file!,
        docTypeId: docTypeId as number,
        uploaderId,
      });

      /* 2. guarda comentarios, si los hay */
      if (comentario.trim() || comentarioInt.trim()) {
        await api.patch(`/documentos/${docId}`, {
          comentario: comentario.trim() || undefined,
          comentarioInterno: CAN_INTERNAL ? comentarioInt.trim() || undefined : undefined,
        });
      }

      /* 3. envía */
      await sendMut.mutateAsync({
        docId,
        recipientIds: recipients.map((r) => r.id).slice(0, singleRecipient ? 1 : undefined),
      });

      showSnack("Documento enviado", "success");

      /* limpiar formulario */
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      setDocType("");
      setRecipients([]);
      setComentario("");
      setComentarioInt("");
      setFileErr(null);
      setDocTypeErr(null);
      setRecipErr(null);
    } catch (e) {
      showSnack("Error al enviar", "error");
    }
  };

  /* ---------- 5. Render ---------- */
  return (
    <Box p={3}>
      <Typography variant="h6" gutterBottom>
        Enviar documento
      </Typography>

      <Grid container spacing={2}>
        {/* PDF ------------------------------------------------ */}
        <Grid item xs={12}>
          <Paper
            ref={dropRef}
            sx={{
              p: 2,
              border: "2px dashed",
              borderColor: theme.palette.divider,
              textAlign: "center",
              cursor: "pointer",
            }}
            onClick={() => document.getElementById("pdf-input")?.click()}
          >
            <input
              hidden
              id="pdf-input"
              ref={fileRef}
              type="file"
              accept="application/pdf"
              onChange={(e) => pickFile(e.currentTarget.files?.[0] ?? null)}
            />
            {file ? (
              <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
                <PictureAsPdf color="error" fontSize="large" />
                <Typography>{file.name}</Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    setFile(null);
                    if (fileRef.current) fileRef.current.value = "";
                  }}
                >
                  <Delete fontSize="inherit" />
                </IconButton>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} justifyContent="center">
                <UploadFile />
                <Typography>Arrastra un PDF aquí o haz clic para examinar</Typography>
              </Stack>
            )}
          </Paper>
          {fileError && (
            <Typography color="error" variant="caption">
              {fileError}
            </Typography>
          )}
        </Grid>

        {/* Tipo ------------------------------------------------ */}
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            required
            label="Tipo de documento"
            value={docTypeId}
            onChange={(e) => {
              setDocType(Number(e.target.value));
              setDocTypeErr(null);
            }}
            disabled={typesLoading}
            error={!!docTypeError}
            helperText={
              docTypeError ??
              (singleRecipient ? "Este tipo requiere firma: solo 1 destinatario" : " ")
            }
          >
            {docTypes.map((t) => (
              <MenuItem key={t.docTypeId} value={t.docTypeId}>
                {t.nombre}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Comentarios públicos / internos ------------------- */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Comentario (visible para el destinatario)"
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
          />
        </Grid>
        {CAN_INTERNAL && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Comentario interno (solo HR / Facturación / Admin)"
              value={comentarioInt}
              onChange={(e) => setComentarioInt(e.target.value)}
            />
          </Grid>
        )}

        {/* Destinatarios ------------------------------------- */}
        <Grid item xs={12}>
          <RecipientsAutocomplete
            value={recipients}
            onChange={(list) => {
              setRecipErr(null);
              setRecipients(singleRecipient ? list.slice(0, 1) : list);
            }}
            error={!!recipError}
            helperText={
              recipError ??
              (singleRecipient ? "Este tipo requiere firma: solo 1 destinatario" : " ")
            }
          />
          <Box mt={1} display="flex" gap={1} flexWrap="wrap">
            <Tooltip title="Añadir destinatarios por grupo">
              <span>
                <Button
                  size="small"
                  startIcon={<GroupAdd />}
                  onClick={() => setRoleDlg(true)}
                  disabled={singleRecipient && recipients.length >= 1}
                >
                  Añadir por grupo
                </Button>
              </span>
            </Tooltip>

            {/* Botón Sincronizar visible solo para roles con permiso */}
            {CAN_INTERNAL && (
              <Tooltip title="Sincronizar documentos del usuario desde Drive">
                <span>
                  <Button size="small" startIcon={<Sync />} onClick={openSyncDlg}>
                    Sincronizar
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        </Grid>

        {/* Enviar -------------------------------------------- */}
        <Grid item xs={12}>
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={uploadMut.isPending || sendMut.isPending}
          >
            {(uploadMut.isPending || sendMut.isPending) && (
              <CircularProgress size={16} sx={{ mr: 1 }} />
            )}
            Enviar
          </Button>
        </Grid>
      </Grid>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.sev}>{snack.msg}</Alert>
      </Snackbar>

      {/* Diálogo grupos */}
      <AddByRoleDialog
        open={roleDlg}
        onClose={() => setRoleDlg(false)}
        onAdded={handleAddByGroup}
      />

      {/* Diálogo sincronización Drive */}
      <Dialog open={syncDlg} onClose={() => setSyncDlg(false)}>
        <DialogTitle>Sincronizar desde Drive</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1, minWidth: 360 }}>
            <TextField
              label="Email del usuario"
              value={syncEmail}
              onChange={(e) => setSyncEmail(e.target.value)}
              fullWidth
              placeholder="usuario@tutorasap.org"
            />
            <FormControlLabel
              control={
                <Checkbox checked={syncCreateTypes} onChange={(_, c) => setSyncCreateTypes(c)} />
              }
              label="Crear tipos de documento si no existen"
            />
            <Typography variant="body2" color="text.secondary">
              La sincronización busca subcarpetas dentro de la carpeta del email. Cada subcarpeta
              debe llamarse como un Tipo de documento.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDlg(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSync} disabled={syncLoading}>
            {syncLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
            Sincronizar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
